import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchPublicRequestableEventsByProduction,
  type PublicRequestableEvent,
} from '@/lib/rta-api.ts';
import {
  findRequestableSelection,
  getEventPrimaryAction,
  getRequestableZonesForEvent,
  PaidTicketSelectionGuard,
  type EventPrimaryAction,
} from './requestable-selection.ts';

/** Inputs needed to coordinate the optional RTA flow with paid booking. */
export interface UseRequestToAttendOptions {
  /** Restaurant/production currently rendered by the widget. */
  restaurantId: number | string | null | undefined;
  /** Event currently selected in the widget. */
  selectedEventId: number | string | null | undefined;
  /** Whether the paid flow currently has inventory for its primary action. */
  hasPaidInventory: boolean;
  /** Clear paid selection state when an RTA zone becomes authoritative. */
  onRequestableSelection: () => void;
}

/** State and race-safe actions exposed to the shared event page. */
export interface UseRequestToAttendResult {
  /** Canonical Gold/Red request rows for the active event. */
  requestableZones: PublicRequestableEvent[];
  /** Selected canonical request row after fail-closed validation. */
  activeRequestableSelection: PublicRequestableEvent | null;
  /** Raw selected row used to render pressed-state feedback. */
  selectedRequestableRow: PublicRequestableEvent | null;
  /** Primary event action after reconciling paid and RTA selection. */
  eventPrimaryAction: EventPrimaryAction;
  /** Return authority to paid booking and clear an RTA selection. */
  selectPaidFlow: () => void;
  /** Make one server-authorized request row authoritative. */
  selectRequestableFlow: (row: PublicRequestableEvent) => void;
  /** Start a paid inventory load and return its race-control token. */
  beginPaidLoad: () => number;
  /** Whether a paid response may update inventory and selection. */
  canApplyPaidLoad: (token: number) => boolean;
  /** Whether a paid response may finish the current loading state. */
  canFinishPaidLoad: (token: number) => boolean;
}

/**
 * Own public RTA eligibility, selection, and paid-load race coordination.
 *
 * Eligibility is entirely server-driven: any production with publicly
 * requestable inventory (the `publicRequestable()` scope) lights up the RTA
 * flow, and the widget fails closed to paid booking when the query is empty.
 *
 * @param options - Current widget identity plus the paid-selection reset hook.
 * @returns Canonical RTA state and the minimal actions needed by EventPage.
 */
export function useRequestToAttend({
  restaurantId,
  selectedEventId,
  hasPaidInventory,
  onRequestableSelection,
}: UseRequestToAttendOptions): UseRequestToAttendResult {
  const [requestableRows, setRequestableRows] = useState<PublicRequestableEvent[]>([]);
  const [selectedRequestableRow, setSelectedRequestableRow] =
    useState<PublicRequestableEvent | null>(null);
  const paidTicketSelectionGuard = useMemo(() => new PaidTicketSelectionGuard(), []);

  const selectPaidFlow = useCallback(() => {
    paidTicketSelectionGuard.selectPaid();
    setSelectedRequestableRow(null);
  }, [paidTicketSelectionGuard]);

  const selectRequestableFlow = useCallback(
    (row: PublicRequestableEvent) => {
      paidTicketSelectionGuard.selectRequestable();
      setSelectedRequestableRow(row);
      onRequestableSelection();
    },
    [onRequestableSelection, paidTicketSelectionGuard]
  );

  useEffect(() => {
    selectPaidFlow();
    if (restaurantId == null) {
      setRequestableRows([]);
      return;
    }

    const controller = new AbortController();
    void fetchPublicRequestableEventsByProduction(String(restaurantId), {
      signal: controller.signal,
    }).then((rows) => {
      if (!controller.signal.aborted) setRequestableRows(rows);
    });

    return () => controller.abort();
  }, [restaurantId, selectPaidFlow]);

  useEffect(() => {
    selectPaidFlow();
  }, [selectedEventId, selectPaidFlow]);

  const requestableZones = useMemo(
    () => getRequestableZonesForEvent(selectedEventId, requestableRows),
    [requestableRows, selectedEventId]
  );
  const activeRequestableSelection = useMemo(
    () =>
      selectedRequestableRow
        ? findRequestableSelection({
            eventId: selectedEventId,
            selectedInventoryId: selectedRequestableRow.event_inventory_id,
            rows: requestableRows,
          })
        : null,
    [requestableRows, selectedEventId, selectedRequestableRow]
  );

  const beginPaidLoad = useCallback(
    () => paidTicketSelectionGuard.beginPaidLoad(),
    [paidTicketSelectionGuard]
  );
  const canApplyPaidLoad = useCallback(
    (token: number) => paidTicketSelectionGuard.canApplyPaidLoad(token),
    [paidTicketSelectionGuard]
  );
  const canFinishPaidLoad = useCallback(
    (token: number) => paidTicketSelectionGuard.canFinishPaidLoad(token),
    [paidTicketSelectionGuard]
  );

  return {
    requestableZones,
    activeRequestableSelection,
    selectedRequestableRow,
    eventPrimaryAction: getEventPrimaryAction({
      activeRequestableSelection,
      hasPaidInventory,
    }),
    selectPaidFlow,
    selectRequestableFlow,
    beginPaidLoad,
    canApplyPaidLoad,
    canFinishPaidLoad,
  };
}
